<div class="container pb-8">
  <div class="wizard-page px-4 md:px-8 mt-20">
    <div class="flex items-center justify-between gap-4 mb-5">
      <div>
        <h2 class="settings-header">New Location</h2>
        <p class="text-grey-700 mt-1">{{ 'Create the location, then configure its audits and user access.' | customerText }}</p>
      </div>
      <button class="btn-inverse" type="button" (click)="finish()">Close</button>
    </div>

    <ol class="stepper" aria-label="New location progress">
      @for (step of ['Details', 'Audits', 'Users & Permissions', 'Review']; track step; let index = $index) {
        <li [class.active]="currentStep === index + 1" [class.complete]="currentStep > index + 1">
          <span class="step-number">{{ index + 1 }}</span>
          <span>{{ step === 'Audits' ? ('audit' | customerTerm:true) : step }}</span>
        </li>
      }
    </ol>

    @if (currentStep === 1) {
      <section class="wizard-card">
        <h3 class="text-xl font-bold mb-4">Location details</h3>
        @if (detailsLoading) {
          <p class="text-grey-700 mb-4">Loading available locations...</p>
        }
        <div class="form-grid">
          <label class="form-row">
            <span>Location name</span>
            <input class="text-box" type="text" [(ngModel)]="request.LocationName" maxlength="200" />
          </label>

          <label class="form-row">
            <span>Parent location</span>
            <p-treeselect
              [(ngModel)]="parentLocationSelection"
              [options]="parentLocationNodes"
              [filter]="true"
              [filterInputAutoFocus]="true"
              [fluid]="true"
              [loading]="detailsLoading"
              filterPlaceholder="Search locations"
              placeholder="Select parent location"
              panelStyleClass="location-parent-tree"
              ariaLabel="Parent location"
              appendTo="body"
              (ngModelChange)="selectParentLocation($event)">
            </p-treeselect>
          </label>
        </div>
        @if (saving) {
          <div class="operation-loading" role="status" aria-live="polite">
            <span class="material-symbols-outlined animate-spin">progress_activity</span>
            <div>
              <strong>Creating location...</strong>
              <p>Please keep this page open while the location and initial access are prepared.</p>
            </div>
          </div>
        }
        <div class="actions-row">
          <button class="btn-primary" type="button" [disabled]="saving || detailsLoading || !request.LocationName.trim()" (click)="saveDetails()">
            {{ saving ? 'Creating...' : 'Create location and continue' }}
          </button>
        </div>
      </section>
    }

    @if (currentStep === 2) {
      <section class="wizard-card">
        <h3 class="text-xl font-bold">{{ 'audit' | customerTerm:true }} and frequencies</h3>
        <p class="text-grey-700 mt-1 mb-4">{{ 'Select the audits available at this location and set their required frequencies.' | customerText }}</p>

        <div class="actions-row between audit-actions-top">
          <span class="text-grey-700">{{ selectedAudits.length }} {{ 'audit' | customerTerm }}(s) selected</span>
          <button class="btn-primary" type="button" [disabled]="saving || auditsLoading || audits.length === 0" (click)="saveAudits()">
            {{ saving ? 'Saving...' : ('Save audits and continue' | customerText) }}
          </button>
        </div>

        <div class="optional-copy-panel">
          <div>
            <strong>Optional: copy from another location</strong>
            <p>{{ 'Use an existing location as a starting point, then adjust the copied audits below.' | customerText }}</p>
          </div>
          <label class="form-row copy-select">
            <span class="sr-only">{{ 'Location to copy audits from' | customerText }}</span>
            <p-treeselect
              [(ngModel)]="auditCopyLocationSelection"
              [options]="auditLocationNodes"
              [filter]="true"
              [filterInputAutoFocus]="true"
              [fluid]="true"
              [showClear]="true"
              [loading]="detailsLoading"
              filterPlaceholder="Search locations"
              placeholder="Choose a location to copy from..."
              panelStyleClass="location-parent-tree"
              [ariaLabel]="'Location to copy audits from' | customerText"
              appendTo="body"
              (ngModelChange)="copyAuditsFromSelection($event)">
            </p-treeselect>
          </label>
        </div>

        @if (auditsLoading) {
          <div class="operation-loading" role="status" aria-live="polite">
            <span class="material-symbols-outlined animate-spin">progress_activity</span>
            <div>
              <strong>{{ 'Loading location audits...' | customerText }}</strong>
              <p>{{ 'The location has been created. Its available audits and frequencies are now being prepared.' | customerText }}</p>
            </div>
          </div>
        }

        @if (!auditsLoading) {
        <h4 class="mt-5 font-semibold">Available {{ 'audit' | customerTerm:true | lowercase }}</h4>
        <div class="table-wrap mt-2">
          <table class="wizard-table">
            <thead>
              <tr>
                <th>{{ 'audit' | customerTerm }}</th>
                <th>Assigned</th>
                <th>Frequency</th>
                <th>Multiplier</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>All {{ 'audit' | customerTerm:true | lowercase }}</strong></td>
                <td><app-toggle-switch [(checked)]="allAudits" (checkedChange)="setAllAudits($event)"></app-toggle-switch></td>
                <td></td><td></td>
              </tr>
              @for (audit of audits; track audit.AuditID) {
                <tr>
                  <td><strong>{{ audit.AuditName }}</strong><small>{{ audit.AuditType }}</small></td>
                  <td><app-toggle-switch [(checked)]="audit.AssignedToLocation" (checkedChange)="updateAllAuditsState()"></app-toggle-switch></td>
                  <td>
                    <select class="compact-input" [ngModel]="audit.Frequency.ID" (ngModelChange)="updateAuditFrequency(audit, $event)" [disabled]="!audit.AssignedToLocation">
                      @for (frequency of frequencies; track frequency.ID) {
                        <option [ngValue]="frequency.ID">{{ frequency.Description }}</option>
                      }
                    </select>
                  </td>
                  <td><input class="compact-input multiplier" type="number" min="1" [(ngModel)]="audit.FrequencyMultiplier" [disabled]="!audit.AssignedToLocation" /></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        }
        <div class="actions-row between">
          <span class="text-grey-700">{{ selectedAudits.length }} audit(s) selected</span>
          <button class="btn-primary" type="button" [disabled]="saving || auditsLoading || audits.length === 0" (click)="saveAudits()">
            {{ saving ? 'Saving...' : 'Save audits and continue' }}
          </button>
        </div>
      </section>
    }

    @if (currentStep === 3) {
      <section class="wizard-card">
        <h3 class="text-xl font-bold">Users and permissions</h3>
        <p class="text-grey-700 mt-1 mb-4">Membership and the permissions each user has at this location are saved together.</p>
        <div class="controls-grid">
          <label class="form-row">
            <span>Copy users and permissions from (optional)</span>
            <select class="text-box" [ngModel]="userCopyLocationID" (ngModelChange)="loadUsersAndPermissions($event)">
              <option [ngValue]="-1">Do not copy</option>
              @for (location of locations; track location.LocationID) {
                <option [ngValue]="location.LocationID">{{ location.LocationName }}</option>
              }
            </select>
          </label>
          <label class="form-row">
            <span>Filter users</span>
            <input class="text-box" type="search" [(ngModel)]="userFilter" placeholder="Name, username or email" />
          </label>
        </div>

        @if (usersLoading) {
          <div class="operation-loading" role="status" aria-live="polite">
            <span class="material-symbols-outlined animate-spin">progress_activity</span>
            <div><strong>Loading users and permissions...</strong></div>
          </div>
        }
        @if (usersLoadError && !usersLoading) {
          <div class="mt-5" role="alert">
            <p class="text-grey-700 mb-3">Users and permissions could not be loaded.</p>
            <button class="btn-inverse" type="button" (click)="loadUsersAndPermissions(userCopyLocationID)">Try again</button>
          </div>
        }
        @if (usersReady && !usersLoading) {
          <div class="table-wrap mt-5">
            <table class="wizard-table permissions-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Access</th>
                  @for (permission of permissionOptions; track permission.field) {
                    <th>{{ permission.label }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (user of filteredUsers; track user.UserID) {
                  <tr [class.unselected]="!user.Selected">
                    <td>
                      <strong>{{ user.DisplayName }}</strong>
                      @if (user.Locked) { <small>Creator access retained during setup</small> }
                    </td>
                    <td><app-toggle-switch [(checked)]="user.Selected" [disabled]="user.Locked"></app-toggle-switch></td>
                    @for (permission of permissionOptions; track permission.field) {
                      <td>
                        <app-toggle-switch
                          [checked]="user.Features[permission.field]"
                          [disabled]="!user.Selected || user.Locked"
                          (checkedChange)="updatePermission(user, permission.field, $event)">
                        </app-toggle-switch>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
        <div class="actions-row between">
          <button class="btn-inverse" type="button" (click)="previousStep()">Back</button>
          <button class="btn-primary" type="button" [disabled]="saving || !usersReady || selectedUsers.length === 0" (click)="saveUsersAndPermissions()">
            {{ saving ? 'Saving...' : 'Save users and permissions' }}
          </button>
        </div>
      </section>
    }

    @if (currentStep === 4) {
      <section class="wizard-card review-card">
        <span class="material-symbols-outlined review-icon">check_circle</span>
        <h3 class="text-2xl font-bold">Location setup complete</h3>
        <p class="text-grey-700">Review the configuration below. It can be amended later through the existing location settings pages.</p>
        <dl class="review-grid">
          <div><dt>Location</dt><dd>{{ createdLocation?.LocationName }}</dd></div>
          <div><dt>Parent</dt><dd>{{ parentName }}</dd></div>
          <div><dt>{{ 'audit' | customerTerm:true }}</dt><dd>{{ selectedAudits.length }}</dd></div>
          <div><dt>Users</dt><dd>{{ selectedUsers.length }}</dd></div>
        </dl>
        <div class="actions-row centered">
          <button class="btn-inverse" type="button" (click)="previousStep()">Back</button>
          <button class="btn-primary" type="button" (click)="finish()">Finish</button>
        </div>
      </section>
    }
  </div>
</div>
